import { AsyncActionState } from '@telstra-connect/standard-actions/async-action';
import { IProductOrder } from '../../../applications/order-tracker/config/order-tracker-new-common/types';

export interface ILoadProductList {
    data: IProductListAPIResponse | null;
    status: AsyncActionState;
    error?: string;
}

export interface ITableConfig {
    currentPage: number,
    pageSize: number
}

export interface IProductOrderFilter {
    label: ProductOrderFilters;
    values: string[]
}

export type ISelectedProductOrderFilters = Record<ProductOrderFilters, string[]>;

export interface ITableFilter {
    id: string;
    label: string;
    name: string;
    value: string;
    checked: boolean;
}

export interface IProductOrderFiltersResponse {
    productFilter: string[],
    statusFilter: string[],
    siteAddressFilter: string[],
    customerFilter: string[]
}

export interface ISortParam {
    sortBy: string,
    sortOrder: string
}

export interface IProductOrderList {
    status: AsyncActionState;
    productList: IProductListAPIResponse | null;
    selectedColumns: ITableColumn[];
    tableConfig: ITableConfig;
    filters: IProductOrderFilter[];
    selectedFilters: ISelectedProductOrderFilters;
    searchText: string;
    sortParam: ISortParam;
    error?: string;
    hideFilterAction?: boolean;
}

export enum ProductOrderFilters {
    productFilter = 'Product',
    statusFilter = 'Product status',
    siteAddressFilter = 'Site address',
    customerFilter = 'Company'
}

export enum ProductsColumnsAccessors {
    OrderNumber = 'orderNumber',
    ProductName = 'productName',
    Status = 'status',
    ActivityType = 'activityType',
    ServiceId = 'serviceId',
    SiteRawAddress = 'siteRawAddress',
    SiteName = 'siteName',
    CustomerName = 'customerName',
    Cidn = 'cidn'
}

export interface ITableColumn {
    Header: string,
    accessor: ProductsColumnsAccessors,
    width: number,
    sortable?: boolean
}

export const SelectedFiltersInitialValues: ISelectedProductOrderFilters = {
    [ProductOrderFilters.productFilter]: [],
    [ProductOrderFilters.statusFilter]: [],
    [ProductOrderFilters.siteAddressFilter]: [],
    [ProductOrderFilters.customerFilter]: [],
}

export interface IDismissibleFilterObject {
    id: string;
    context: ProductOrderFilters,
    label: string
}

export const OrderListOriginalColumns: ITableColumn[] = [
    {
        Header: 'Order number',
        accessor: ProductsColumnsAccessors.OrderNumber,
        width: 200,
        sortable: true,
    },
    {
        Header: 'Product',
        accessor: ProductsColumnsAccessors.ProductName,
        width: 400,
        sortable: true,
    },
    {
        Header: 'Product status',
        accessor: ProductsColumnsAccessors.Status,
        width: 202,
        sortable: true,
    },
    {
        Header: 'Activity type',
        accessor: ProductsColumnsAccessors.ActivityType,
        width: 182,
        sortable: true,
    },
    {
        Header: 'Service ID',
        accessor: ProductsColumnsAccessors.ServiceId,
        width: 242,
        sortable: true,
    },
    {
        Header: 'Site address',
        accessor: ProductsColumnsAccessors.SiteRawAddress,
        width: 286,
        sortable: true,
    },
    {
        Header: 'Site name',
        accessor: ProductsColumnsAccessors.SiteName,
        width: 200,
        sortable: true,
    },
    {
        Header: 'Company',
        accessor: ProductsColumnsAccessors.CustomerName,
        width: 200,
        sortable: true,
    },
    {
        Header: 'Customer ID',
        accessor: ProductsColumnsAccessors.Cidn,
        width: 200,
        sortable: true,
    },
];

export const DefaultColumns = [
    ProductsColumnsAccessors.OrderNumber,
    ProductsColumnsAccessors.ProductName,
];

export interface IProductListAPIResponse {
    products: IProductOrder[];
    totalProducts: number;
}

export interface IProductListAPIPostBody {
  pageNumber: number;
  pageSize?: number;
  salesOrderNumber?: string;
  productOrderId?: string;
  orderStatus?: string;
  sortBy?: string;
  sortOrder?: string;
  searchBar?: string;
  products?: string[];
  siteAddresses?: string[];
  customers?: string[];
}

export enum OrderTrackerDownloadFileTypes {
  Csv = 'csv',
  Pdf = 'pdf'
}
export interface IDownloadProductList {
  status: AsyncActionState;
  fileType?: OrderTrackerDownloadFileTypes;
  error?: string;
}

export type DataViewFields = 'orderNumber' | 'product' | 'status' | 'activityType' |
'serviceID' | 'siteAddress' | 'customer' | 'siteName' | 'customerID';

export const DataViewFieldsMapping: Record<ProductsColumnsAccessors, DataViewFields>  =  {
    [ProductsColumnsAccessors.OrderNumber]: 'orderNumber',
    [ProductsColumnsAccessors.ProductName]: 'product',
    [ProductsColumnsAccessors.Status]: 'status',
    [ProductsColumnsAccessors.ActivityType]: 'activityType',
    [ProductsColumnsAccessors.ServiceId]: 'serviceID',
    [ProductsColumnsAccessors.SiteRawAddress]: 'siteAddress',
    [ProductsColumnsAccessors.CustomerName]: 'customer',
    [ProductsColumnsAccessors.SiteName]: 'siteName',
    [ProductsColumnsAccessors.Cidn]: 'customerID'
} as const;

export enum SortOrders {
  ascending = 'ASC',
  descending = 'DESC'
}

export interface IDownloadProductListPayload {
  fileType: OrderTrackerDownloadFileTypes,
  dataView: DataViewFields[];
  orderStatus: string;
  products: string[];
  siteAddresses: string[];
  customers: string[];
  sortBy?: string;
  sortOrder?: SortOrders;
  searchBar: string;
}

export interface IDownloadProductListResponse {
  blob: Blob;
  fileName: string;
}

export interface ISelectedFilters {
  currentPage: number,
  pageSize: number
}

export enum ErrorTypes {
    NoOrders = 'No orders to track',
    NoResults = 'No results found',
    ApiFailure = 'Data not available right now'
}

export type ProductSortFields = 'salesOrderNumber' | 'name' | 'derivedStatus' | 'actionType' |
'serviceId' | 'customerName' | 'siteAddress' | 'siteName' | 'cidn';

export const ProductSortFieldsMapping: Record<string, ProductSortFields>  =  {
    [ProductsColumnsAccessors.OrderNumber]: 'salesOrderNumber',
    [ProductsColumnsAccessors.ProductName]: 'name',
    [ProductsColumnsAccessors.Status]: 'derivedStatus',
    [ProductsColumnsAccessors.ActivityType]: 'actionType',
    [ProductsColumnsAccessors.ServiceId]: 'serviceId',
    [ProductsColumnsAccessors.CustomerName]: 'customerName',
    [ProductsColumnsAccessors.SiteName]: 'siteName',
    [ProductsColumnsAccessors.SiteRawAddress]: 'siteAddress',
    [ProductsColumnsAccessors.Cidn]: 'cidn'
} as const;
